import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { authAxios } from '../utils/api';
import { Mail, Bell, BellOff, Info, Loader2, MessageSquare, AlertCircle } from 'lucide-react';

export default function EmailPreferences({ userId = null, showHeader = true }) {
  const { API, user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [preferences, setPreferences] = useState([]);
  const [helperText, setHelperText] = useState('');
  const [targetUser, setTargetUser] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [smsConsented, setSmsConsented] = useState(false);

  // Determine if we're viewing own preferences or another user's
  const isOwnPreferences = !userId || userId === user?.id;
  const endpoint = isOwnPreferences 
    ? `${API}/me/email-preferences` 
    : `${API}/users/${userId}/email-preferences`;

  useEffect(() => {
    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(endpoint);
      setPreferences(response.data.preferences || []);
      setHelperText(response.data.helper_text || '');
      setTargetUser(response.data.user || null);
      setCanEdit(response.data.can_edit !== false);
      // Check if user has SMS consent (either opted in via app or verbal consent from admin)
      const userData = response.data.user;
      setSmsConsented(userData?.sms_opted_in || userData?.sms_verbal_consent || false);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view these preferences");
      } else {
        toast.error('Failed to load notification preferences');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (emailId, channel, currentValue) => {
    if (!canEdit) {
      toast.error("You don't have permission to edit these preferences");
      return;
    }

    const newValue = !currentValue;
    const savingKey = `${emailId}_${channel}`;
    
    // Optimistic update
    setPreferences(prev => 
      prev.map(p => {
        if (p.email_id === emailId) {
          return channel === 'sms' 
            ? { ...p, sms_enabled: newValue }
            : { ...p, is_enabled: newValue };
        }
        return p;
      })
    );

    try {
      setSaving(prev => ({ ...prev, [savingKey]: true }));
      const updateEndpoint = isOwnPreferences 
        ? `${API}/me/email-preferences` 
        : `${API}/users/${userId}/email-preferences`;
      
      const prefUpdate = channel === 'sms'
        ? { email_id: emailId, sms_enabled: newValue }
        : { email_id: emailId, is_enabled: newValue };
      
      await authAxios.patch(updateEndpoint, {
        preferences: [prefUpdate]
      });
      
      const channelName = channel === 'sms' ? 'SMS' : 'Email';
      toast.success(newValue ? `${channelName} notifications enabled` : `${channelName} notifications disabled`);
    } catch (error) {
      // Revert optimistic update
      setPreferences(prev => 
        prev.map(p => {
          if (p.email_id === emailId) {
            return channel === 'sms'
              ? { ...p, sms_enabled: currentValue }
              : { ...p, is_enabled: currentValue };
          }
          return p;
        })
      );
      console.error('Failed to update preference:', error);
      toast.error('Failed to update preference');
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  // Group preferences by category
  const triggerPrefs = preferences.filter(p => p.category === 'triggers');
  const dripPrefs = preferences.filter(p => p.category === 'drip_campaigns');
  
  // Check if any preferences have SMS configured
  const hasSmsPrefs = preferences.some(p => p.has_sms);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (preferences.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Mail className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No configurable notifications available</p>
          <p className="text-sm text-slate-400 mt-1">
            Notifications will appear here once they are configured by the administrator
          </p>
        </CardContent>
      </Card>
    );
  }

  // Render a preference row for tabular view
  const PreferenceRow = ({ pref }) => {
    const hasEmail = pref.has_email !== false;
    const hasSms = pref.has_sms === true;
    const smsDisabled = !smsConsented || saving[`${pref.email_id}_sms`] || !canEdit;
    
    return (
      <tr className="border-b border-slate-200 last:border-0">
        <td className="py-3 pr-4">
          <div>
            <span className="font-medium text-slate-900 text-sm">{pref.name}</span>
            <p className="text-xs text-slate-500 mt-0.5">{pref.description}</p>
          </div>
        </td>
        <td className="py-3 px-3 text-center">
          {hasEmail ? (
            <Switch
              checked={pref.is_enabled}
              onCheckedChange={() => handleToggle(pref.email_id, 'email', pref.is_enabled)}
              disabled={saving[`${pref.email_id}_email`] || !canEdit}
              data-testid={`toggle-email-${pref.email_id}`}
            />
          ) : (
            <span className="text-slate-300 text-xs">—</span>
          )}
        </td>
        {hasSmsPrefs && (
          <td className="py-3 px-3 text-center">
            {hasSms ? (
              <Switch
                checked={smsConsented ? pref.sms_enabled : false}
                onCheckedChange={() => handleToggle(pref.email_id, 'sms', pref.sms_enabled)}
                disabled={smsDisabled}
                className={!smsConsented ? 'opacity-40' : ''}
                data-testid={`toggle-sms-${pref.email_id}`}
              />
            ) : (
              <span className="text-slate-300 text-xs">—</span>
            )}
          </td>
        )}
      </tr>
    );
  };

  // Render a preference table
  const PreferenceTable = ({ prefs, title, description }) => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* SMS consent warning */}
        {hasSmsPrefs && !smsConsented && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">SMS notifications require consent.</span>
              {isOwnPreferences ? (
                <span> Visit your <a href="/profile" className="underline">profile settings</a> to add your phone number and consent to SMS.</span>
              ) : (
                <span> The player must consent to SMS via the app, or you can record verbal consent in the Profile tab.</span>
              )}
            </div>
          </div>
        )}
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2">
                Notification
              </th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2 w-20">
                <div className="flex items-center justify-center gap-1">
                  <Mail className="w-3 h-3" />
                  <span>Email</span>
                </div>
              </th>
              {hasSmsPrefs && (
                <th className={`text-center text-xs font-semibold uppercase tracking-wider pb-2 w-20 ${!smsConsented ? 'text-slate-300' : 'text-slate-500'}`}>
                  <div className="flex items-center justify-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    <span>SMS</span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {prefs.map((pref) => (
              <PreferenceRow key={pref.email_id} pref={pref} />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Notification Preferences
          </h2>
          {targetUser && !isOwnPreferences && (
            <p className="text-slate-600 mt-1">
              Managing preferences for: <span className="font-medium">{targetUser.name || targetUser.email}</span>
            </p>
          )}
        </div>
      )}

      {/* Helper Text */}
      {helperText && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">{helperText}</p>
        </div>
      )}

      {/* Event-based Notifications */}
      {triggerPrefs.length > 0 && (
        <PreferenceTable 
          prefs={triggerPrefs}
          title="Event Notifications"
          description="Notifications triggered by specific actions or events"
        />
      )}

      {/* Drip Campaign Notifications */}
      {dripPrefs.length > 0 && (
        <PreferenceTable 
          prefs={dripPrefs}
          title="Reminder Notifications"
          description="Helpful reminders to complete your setup"
        />
      )}

      {/* Disabled state message */}
      {!canEdit && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <BellOff className="w-4 h-4" />
          <span>You can only view these preferences. Contact an administrator to make changes.</span>
        </div>
      )}
    </div>
  );
}
